// Curated MySQL keyword list emitted by `mysqlResolver`. Tier 7 per
// RESEARCH.md §5.2. The orchestrator's `applyKeywordCasingPostProcess`
// transforms label/insertText/filterText to the user's preferred casing —
// labels stay UPPER here so the dominant-case fallback has a stable
// reference and matches the conventional rendering.
//
// Selection criteria: MySQL keywords that surface in normal user authoring
// flows (DML, DDL, query-shaping, transactional control, common modern
// MySQL forms like CTE / WINDOW / RETURNING-on-MariaDB / JSON_TABLE).
// Reserved-but-rare words that only appear in system-generated DDL are
// excluded.
//
// F5.3 Wave-4 R-bucket additions — kept inside the curated list rather than
// gated to a sub-position because the resolver collapses several procedural
// / DDL contexts (`CREATE TRIGGER … |BEFORE`, `INSERT |LOW_PRIORITY`,
// `DECLARE |CONTINUE HANDLER`, `SIGNAL SQLSTATE … SET |MESSAGE_TEXT`,
// `FROM t |FORCE INDEX`, `EXTRACT(|YEAR_MONTH FROM …)`, trigger-body
// `NEW`/`OLD`) to `positionKind === 'unknown'` and the resolver's `unknown`
// branch emits the full curated keyword set at tier 7. Adding these
// dialect tokens here makes them surface in the suggest model without
// growing a per-context keyword table. Members:
//   • INSERT modifiers — LOW_PRIORITY, DELAYED, HIGH_PRIORITY
//   • Index-hint — FORCE (USE / IGNORE already covered)
//   • Trigger timing / pseudo-relations — BEFORE, AFTER, NEW, OLD
//   • Handler-action — CONTINUE, UNDO (EXIT already covered)
//   • SIGNAL / RESIGNAL condition-info items — MESSAGE_TEXT, MYSQL_ERRNO,
//     CLASS_ORIGIN, SUBCLASS_ORIGIN, CONSTRAINT_CATALOG, CONSTRAINT_SCHEMA,
//     CONSTRAINT_NAME, CATALOG_NAME, SCHEMA_NAME, TABLE_NAME, COLUMN_NAME,
//     CURSOR_NAME
//   • EXTRACT compound units — YEAR_MONTH, DAY_HOUR, DAY_MINUTE, DAY_SECOND,
//     HOUR_MINUTE, HOUR_SECOND, MINUTE_SECOND

const MYSQL_KEYWORD_LIST: readonly string[] = [
  'AFTER', 'ALL', 'ALTER', 'ANALYZE', 'AND', 'ANY', 'AS', 'ASC',
  'AUTO_INCREMENT', 'BEFORE', 'BEGIN', 'BETWEEN', 'BIGINT', 'BINARY',
  'BLOB', 'BOOLEAN', 'BOTH', 'BY', 'CALL', 'CASE', 'CAST',
  'CATALOG_NAME', 'CHAR', 'CHARSET', 'CHECK',
  'CLASS_ORIGIN', 'COLLATE', 'COLUMN', 'COLUMN_NAME', 'COMMIT',
  'CONSTRAINT', 'CONSTRAINT_CATALOG', 'CONSTRAINT_NAME',
  'CONSTRAINT_SCHEMA', 'CONTINUE', 'CREATE', 'CROSS', 'CURRENT_DATE',
  'CURRENT_TIME', 'CURRENT_TIMESTAMP', 'CURRENT_USER',
  'CURSOR_NAME', 'DATABASE', 'DATE', 'DATETIME', 'DAY',
  'DAY_HOUR', 'DAY_MINUTE', 'DAY_SECOND',
  'DECIMAL', 'DECLARE', 'DEFAULT', 'DELAYED',
  'DELETE', 'DESC', 'DESCRIBE', 'DISTINCT', 'DISTINCTROW', 'DIV', 'DOUBLE',
  'DROP', 'DUAL', 'ELSE', 'ELSEIF', 'END', 'ENGINE', 'ENUM',
  'EVENT', 'EXISTS', 'EXIT', 'EXPLAIN', 'FALSE', 'FETCH',
  'FLOAT', 'FOR', 'FORCE', 'FOREIGN', 'FROM', 'FULL', 'FULLTEXT',
  'FUNCTION', 'GRANT', 'GROUP', 'HAVING', 'HIGH_PRIORITY',
  'HOUR', 'HOUR_MINUTE', 'HOUR_SECOND', 'IF',
  'IGNORE', 'IN', 'INDEX', 'INNER', 'INOUT', 'INSERT', 'INT',
  'INTEGER', 'INTERVAL', 'INTO', 'IS', 'ITERATE', 'JOIN',
  'JSON', 'KEY', 'LEAVE', 'LEFT', 'LIKE', 'LIMIT', 'LOCK',
  'LONG', 'LONGBLOB', 'LONGTEXT', 'LOOP', 'LOW_PRIORITY',
  'MATCH', 'MEDIUMINT',
  'MEDIUMTEXT', 'MESSAGE_TEXT', 'MINUTE', 'MINUTE_SECOND',
  'MOD', 'MODIFIES', 'MONTH', 'MYSQL_ERRNO', 'NATURAL',
  'NCHAR', 'NEW', 'NOT', 'NULL', 'NUMERIC', 'OFFSET', 'OLD', 'ON',
  'OPTIMIZE', 'OR',
  'ORDER', 'OUT', 'OUTER', 'OVER', 'PARTITION', 'PRECISION',
  'PRIMARY', 'PROCEDURE', 'RANGE', 'READ', 'READS', 'REAL',
  'RECURSIVE', 'REFERENCES', 'REGEXP', 'RELEASE', 'RENAME',
  'REPEAT', 'REPLACE', 'RESIGNAL', 'RETURN', 'RETURNING',
  'RETURNS', 'REVOKE', 'RIGHT', 'RLIKE', 'ROLLBACK', 'ROW',
  'ROWS', 'SAVEPOINT', 'SCHEMA', 'SCHEMA_NAME',
  'SECOND', 'SELECT', 'SET',
  'SHOW', 'SIGNAL', 'SIGNED', 'SMALLINT', 'SQL', 'SQL_BIG_RESULT',
  'SQL_BUFFER_RESULT', 'SQL_CALC_FOUND_ROWS', 'SQL_NO_CACHE',
  'SQL_SMALL_RESULT', 'START', 'STORED',
  'STRAIGHT_JOIN', 'SUBCLASS_ORIGIN', 'TABLE', 'TABLE_NAME',
  'TEMPORARY', 'TEXT', 'THEN', 'TIME',
  'TIMESTAMP', 'TINYINT', 'TINYTEXT', 'TO', 'TRAILING',
  'TRANSACTION', 'TRIGGER', 'TRUE', 'TRUNCATE',
  'UNDO', 'UNION',
  'UNIQUE', 'UNSIGNED', 'UPDATE', 'USE', 'USING', 'UTC_DATE',
  'UTC_TIME', 'UTC_TIMESTAMP', 'VALUES', 'VARCHAR', 'VARYING',
  'VIEW', 'VIRTUAL', 'WHEN', 'WHERE', 'WHILE', 'WINDOW',
  'WITH', 'WITHOUT', 'WRITE', 'XOR', 'YEAR', 'YEAR_MONTH', 'ZEROFILL',
];

export const MYSQL_KEYWORDS: ReadonlySet<string> = new Set(MYSQL_KEYWORD_LIST);

/**
 * Position-aware keyword list. Today returns the whole curated set — the
 * orchestrator's quality-filter trims the popup. A future per-position
 * refinement can split this into clause-start vs general subsets.
 */
export function getMysqlKeywordsForPosition(): readonly string[] {
  return MYSQL_KEYWORD_LIST;
}

/**
 * Multi-word MySQL keywords typed one word at a time — see
 * `compoundKeywordRemainder.ts`. Only compounds
 * `context/legalNextKeywords.ts` does not already carry for mysql belong here.
 * Sources:
 *   • Routine characteristics (MySQL docs, "CREATE PROCEDURE and CREATE
 *     FUNCTION Statements"): the characteristic clause set the resolver emits
 *     whole at the header slot (`MYSQL_ROUTINE_CHARACTERISTIC_KEYWORDS` in
 *     `mysqlResolver.ts`, coverage script.45 §3) — nothing completed them once
 *     the first word was written (`READS S▮`).
 *   • `UNBOUNDED FOLLOWING` — window frame END bound (MySQL docs, "Window
 *     Function Concepts and Syntax"); the per-slot model carries only the
 *     frame-START vocabulary and the direction-scoped lists live in
 *     `mysqlResolver.ts` (`MYSQL_FRAME_START_KEYWORDS` /
 *     `MYSQL_FRAME_END_KEYWORDS`).
 */
export const MYSQL_COMPOUND_KEYWORDS: readonly string[] = [
  'NOT DETERMINISTIC',
  'LANGUAGE SQL',
  'CONTAINS SQL',
  'NO SQL',
  'READS SQL DATA',
  'MODIFIES SQL DATA',
  'SQL SECURITY DEFINER',
  'SQL SECURITY INVOKER',
  'UNBOUNDED FOLLOWING',
];
